import { useEffect, useState } from 'react';
import { Button, Card, Divider, Link } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import {
  DocumentTemplateType,
  IBidSetup,
  ProjectBiddingSummaryType,
  WorkflowStatusType,
} from '../../api-client/autogenerated';
import { OpenInNew } from '@material-ui/icons';
import { documentTypeToUrl } from '../../scripts/utils';
import { useHistory } from 'react-router';
import { getProjectBiddingSummaryById } from '../../models/api/project';
import CircularLoader from '../../main-components/loader/CircularLoader';

const useStyles = makeStyles({
  gridContainer: {
    display: 'grid',
    gap: 16,

    gridTemplateColumns: '1fr',

    '@media (min-width:460px)': {
      gridTemplateColumns: '1fr 1fr',
    },

    '@media (min-width:925px)': {
      gridTemplateColumns: '1fr 1fr 1fr 1fr',
    },
  },
  titleStyle: {
    background: 'linear-gradient(225deg, #00308C 0%, #002366 100%)',
    borderRadius: '4px 4px 0px 0px',
  },
  button: {
    flex: '1 1 0',
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: 4,
    paddingTop: 8,
    borderRadius: 8,
    border: '1px solid',
    backgroundColor: 'rgba(9,71,185,0.4)',
  },
  buttonHover: {
    flex: '1 1 0',
    display: 'flex',
    flexDirection: 'column',
    padding: 8,
    borderRadius: 8,
    border: '1px solid',
    backgroundColor: 'rgba(9,71,185,0.4)',
    '&:hover': {
      cursor: 'pointer',
      backgroundColor: '#6289d2',
    },
  },
  buttonTitle: {
    fontSize: 18,
    fontWeight: 500,
  },
  buttonDividerHorizontal: {
    marginTop: '0px !important',
    marginBottom: '0px !important',
    marginRight: 8,
    backgroundColor: '#464546',
  },
  buttonDividerVertical: {
    height: '28px !important',
    marginTop: '-4px !important',
    marginLeft: '10px !important',
    marginRight: 8,
    backgroundColor: '#464546',
  },
  buttonRow: {
    display: 'flex',
    height: 28,
    paddingTop: 4,
    marginRight: 8,
    '&:hover': {
      cursor: 'pointer',
      backgroundColor: 'rgba(9,71,185,0.4)',
    },
  },
  buttonRowNoHover: {
    display: 'flex',
    height: 28,
    paddingTop: 4,
    marginRight: 8,
  },
  buttonLabelNumber: {
    textAlign: 'right',
    width: 40,
    display: 'inline-block',
  },
  link: {
    fontSize: 16,
    lineHeight: 0,
  },
});

interface Props {
  bidSetup: IBidSetup;
}

export default function BiddingSummary(props: Props) {
  const { bidSetup } = props;
  const classes = useStyles();
  const history = useHistory();

  const [summary, setSummary] = useState<ProjectBiddingSummaryType>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const project = bidSetup.project;

  const handleBidMgmtLink = () => {
    const newWindow = window.open(
      `${window.location.origin}/main/projects/${project?.id}/bid-mgmt`,
      '_blank',
      'noopener,noreferrer',
    );
    if (newWindow) newWindow.opener = null;
  };

  const handleDocumentIndexLink = (docType: DocumentTemplateType, status?: string) => {
    history.push(
      `/main/projects/${project?.id}/documents/${documentTypeToUrl[docType]}`,
      status && {
        status,
      },
    );
  };

  const handleBidSubmissionLink = () => {
    history.push(`/main/projects/${project?.id}/documents/bid-submissions`);
  };

  const handlePlanholderLink = () => {
    history.push(`/main/projects/${project?.id}/documents/planholder-list`);
  };

  useEffect(() => {
    if (project) {
      setIsLoading(true);
      getProjectBiddingSummaryById(project.id).then((summary: ProjectBiddingSummaryType) => {
        setIsLoading(false);
        setSummary(summary);
      });
    }
  }, [project]);

  return (
    <Card style={{ height: '100%', color: '#464546' }}>
      <CardContent className={classes.titleStyle}>
        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <h2
            style={{
              textAlign: 'left',
              color: '#FFFFFF',
              margin: 0,
              lineHeight: 1,
              fontSize: 26,
            }}
          >
            Progress Summary (Not visible to public)
          </h2>

          {project?.id && (
            <Button className={classes.link} style={{ padding: 0 }}>
              <Link onClick={handleBidMgmtLink}>
                <span style={{ color: 'white' }}> View in CENTERLINE</span>
                <OpenInNew
                  style={{ fontSize: 18, marginLeft: 4, marginBottom: 4, color: 'white' }}
                />
              </Link>
            </Button>
          )}
        </div>
      </CardContent>
      <CardContent>
        {isLoading || !project ? (
          <CircularLoader />
        ) : (
          <div className={classes.gridContainer}>
            <div onClick={handlePlanholderLink} className={classes.buttonHover}>
              <Typography className={classes.buttonTitle}>Planholders</Typography>
              <Divider className={classes.buttonDividerHorizontal} />
              <div className={classes.buttonRowNoHover}>
                <Typography className={classes.buttonLabelNumber}>
                  {summary?.planholders.numUsers}
                </Typography>
                <Divider orientation="vertical" className={classes.buttonDividerVertical} />
                <Typography>Planholders</Typography>
              </div>
              <div className={classes.buttonRowNoHover}>
                <Typography className={classes.buttonLabelNumber}>
                  {summary?.planholders.numViews}
                </Typography>
                <Divider orientation="vertical" className={classes.buttonDividerVertical} />
                <Typography>Views</Typography>
              </div>
              <div className={classes.buttonRowNoHover}>
                <Typography className={classes.buttonLabelNumber}>
                  {summary?.planholders.numDownloads}
                </Typography>
                <Divider orientation="vertical" className={classes.buttonDividerVertical} />
                <Typography>Downloads</Typography>
              </div>
            </div>
            <div onClick={handleBidSubmissionLink} className={classes.buttonHover}>
              <Typography className={classes.buttonTitle}>Prime Bidders</Typography>
              <Divider className={classes.buttonDividerHorizontal} />
              <div className={classes.buttonRowNoHover}>
                <Typography className={classes.buttonLabelNumber}>
                  {summary?.numPrimeBidders}
                </Typography>
                <Divider orientation="vertical" className={classes.buttonDividerVertical} />
                <Typography>Prime Bidders</Typography>
              </div>
              <div className={classes.buttonRowNoHover}>
                <Typography className={classes.buttonLabelNumber}>
                  {summary?.numBidSubmissions}
                </Typography>
                <Divider orientation="vertical" className={classes.buttonDividerVertical} />
                <Typography>Bids Submissions</Typography>
              </div>
            </div>
            <div className={classes.button}>
              <Typography className={classes.buttonTitle}>Bidder RFIs</Typography>
              <Divider className={classes.buttonDividerHorizontal} />
              <div
                onClick={() =>
                  handleDocumentIndexLink(
                    DocumentTemplateType.BidderRfIs,
                    WorkflowStatusType.SubmittedForReview,
                  )
                }
                className={classes.buttonRow}
              >
                <Typography className={classes.buttonLabelNumber}>
                  {summary?.bidderRfis.numNew}
                </Typography>
                <Divider orientation="vertical" className={classes.buttonDividerVertical} />
                <Typography>New</Typography>
              </div>
              <div
                onClick={() =>
                  handleDocumentIndexLink(
                    DocumentTemplateType.BidderRfIs,
                    WorkflowStatusType.UnderReview,
                  )
                }
                className={classes.buttonRow}
              >
                <Typography className={classes.buttonLabelNumber}>
                  {summary?.bidderRfis.numUnderReview}
                </Typography>
                <Divider orientation="vertical" className={classes.buttonDividerVertical} />
                <Typography>Under Review</Typography>
              </div>
              <div
                onClick={() => handleDocumentIndexLink(DocumentTemplateType.BidderRfIs, 'overdue')}
                className={classes.buttonRow}
              >
                <Typography className={classes.buttonLabelNumber}>
                  {summary?.bidderRfis.numOverdue}
                </Typography>
                <Divider orientation="vertical" className={classes.buttonDividerVertical} />
                <Typography>Overdue</Typography>
              </div>
            </div>
            <div className={classes.button}>
              <Typography className={classes.buttonTitle}>Substitution Requests</Typography>
              <Divider className={classes.buttonDividerHorizontal} />
              <div
                onClick={() =>
                  handleDocumentIndexLink(
                    DocumentTemplateType.SubstitutionRequests,
                    WorkflowStatusType.SubmittedForReview,
                  )
                }
                className={classes.buttonRow}
              >
                <Typography className={classes.buttonLabelNumber}>
                  {summary?.substRequests.numNew}
                </Typography>
                <Divider orientation="vertical" className={classes.buttonDividerVertical} />
                <Typography>New</Typography>
              </div>
              <div
                onClick={() =>
                  handleDocumentIndexLink(
                    DocumentTemplateType.SubstitutionRequests,
                    WorkflowStatusType.UnderReview,
                  )
                }
                className={classes.buttonRow}
              >
                <Typography className={classes.buttonLabelNumber}>
                  {summary?.substRequests.numUnderReview}
                </Typography>
                <Divider orientation="vertical" className={classes.buttonDividerVertical} />
                <Typography>Under Review</Typography>
              </div>
              <div
                onClick={() =>
                  handleDocumentIndexLink(DocumentTemplateType.SubstitutionRequests, 'overdue')
                }
                className={classes.buttonRow}
              >
                <Typography className={classes.buttonLabelNumber}>
                  {summary?.substRequests.numOverdue}
                </Typography>
                <Divider orientation="vertical" className={classes.buttonDividerVertical} />
                <Typography>Overdue</Typography>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
